import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { forkJoin, Observable, Subject, Subscription } from 'rxjs';
import { map, mergeMap, takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import * as dayjs from 'dayjs';

import { ICruiseSearchParams } from '../../../booking-flow/cruise/cruise-search-page/interfaces/icruise-search-params';
import { ICruisePrefetchParams } from '../../../shared/interfaces/icruise-prefetch-params';
import { ICruisePrefetch } from '../../../shared/interfaces/icruise-prefetch';
import { LoadingItem } from '../../../shared/interfaces/loading-item';

import { EngineDefaultParams } from '../../models/engine-default-params';
import { CruiseSearchService } from 'src/app/modules/booking-flow/cruise/cruise-search-page/services/cruise-search.service';
import { CruisePrefetchService } from '../../../shared/services/cruise-prefetch.service';
import { ProjectService } from '../../../shared/services/project.service';
import { MappingService } from '../../../shared/services/mapping.service';
import { LoadingService } from '../../../shared/services/loading.service';
import sanitizeForUrl from '../../../../utils/sanitizeForUrl';

@Component({
  selector: 'app-engine-cruise',
  templateUrl: './engine-cruise.component.html',
  styleUrls: ['./engine-cruise.component.scss'],
})
export class EngineCruiseComponent implements OnInit, OnDestroy {
  @Input() cruiseSearchParams: ICruiseSearchParams;

  constructor(
    private _cruisePrefetchService: CruisePrefetchService,
    private _cruiseSearchService: CruiseSearchService,
    private _router: Router,
    private _loadingService: LoadingService,
    private _projectService: ProjectService,
    private _mappingService: MappingService
  ) {}

  cruisePrefetch$: Observable<ICruisePrefetch>;
  selectedItinerary: number;
  selectedHarbour: number;
  selectedShip: number;
  destinationsIds: number[];
  selectedMonth: string;

  prefetchParameters: ICruisePrefetchParams;
  private unsubscribe$ = new Subject();

  ngOnInit(): void {
    this.mapAllDestinationsAsIdsArrayAndDoPrefetch();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  mapAllDestinationsAsIdsArrayAndDoPrefetch(): any {
    return this._mappingService.getCruiseMappings().subscribe((allObj) => {
      this.destinationsIds = Object.keys(allObj.itineraries.objects).map((id) =>
        Number(id)
      );
      this.doPrefetch();
    });
  }

  doPrefetch(): void {
    this.cruisePrefetch$ = forkJoin([
      this._projectService.getAgencyNetwork(),
      this._projectService.hasOnlyNetQuotes(),
    ]).pipe(
      map((e) => this.createPrefetchParameters(e[0].id_network, e[1])),
      mergeMap((e) => this._cruisePrefetchService.prefetch(e))
    );
  }

  createPrefetchParameters(
    network?: string,
    onlyNetworkQuotes?: boolean
  ): ICruisePrefetchParams {
    let dateFrom = null;
    let dateTo = null;
    let harbour = null;
    let ship = null;
    // se le destination non arrivano dalla chiamata "all" (this.mapAllDestinationsAsIdsArrayAndDoPrefetch) assegna le destination di default
    let destinationList = this.destinationsIds?.length
      ? this.destinationsIds
      : EngineDefaultParams.defaultItineraries;
    if (this.selectedMonth) {
      const tmpDate = dayjs(this.selectedMonth, 'MM-YYYY');
      dateFrom = tmpDate.startOf('month');
      dateTo = tmpDate.endOf('month');
    }
    if (this.selectedItinerary) {
      destinationList = [this.selectedItinerary];
    }
    if (this.selectedHarbour) {
      harbour = [this.selectedHarbour];
    }
    if (this.selectedShip) {
      ship = [this.selectedShip];
    }

    const ret: ICruisePrefetchParams = {
      date_from: dateFrom,
      date_to: dateTo,
      destinationList,
      durationMax: null,
      durationMin: null,
      harbour,
      ship,
      top: null,
    };

    if (onlyNetworkQuotes) {
      ret.onlyNetworkQuotes = onlyNetworkQuotes;
      ret.network = network;
    }
    return ret;
  }

  onSelectedItinerary(itinerary: number): void {
    this.selectedItinerary = itinerary;
    this.doPrefetch();
  }

  onSelectedDate(date: string): void {
    this.selectedMonth = date;
    this.doPrefetch();
  }

  onSelectedHarbour(harbour: number): void {
    this.selectedHarbour = harbour;
    this.doPrefetch();
  }

  onSelectedShip(ship: number): void {
    this.selectedShip = ship;
    this.doPrefetch();
  }

  onInputSelection(): void {
    document.querySelector('#searchEngine').scrollIntoView();
  }

  searchCruise(): void {
    const loadingItem: LoadingItem = {
      id: 'cruise-pre-search',
      message: 'Caricamento in corso...',
    };
    this._loadingService.addLoad(loadingItem);

    forkJoin([
      this._mappingService.getItineraryById(this.selectedItinerary),
      this._mappingService.getShipyID(this.selectedShip),
      this._mappingService.getHarbourByID(this.selectedHarbour),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((e) => {
        this._loadingService.removeLoad(loadingItem);
        const [itinerary, ship, harbour] = e;
        const itinerarySearch = this.selectedItinerary || 'null';
        const itineraryName = itinerary?.Name || 'tutti';
        const shipSearch = this.selectedShip || 'null';
        const shipName = ship?.Name || 'tutte';
        const monthSearch = this.selectedMonth || 'null';
        const monthName = this.selectedMonth || 'tutte';
        const harbourSearch = this.selectedHarbour || 'null';
        const harbourName = harbour?.Name || 'tutti';
        this._router.navigate([
          `/crociere/ricerca/${itinerarySearch}/${shipSearch}/${harbourSearch}/itinerario/${sanitizeForUrl(
            itineraryName
          )}/nave/${sanitizeForUrl(shipName)}/porto/${sanitizeForUrl(
            harbourName
          )}/il/${monthSearch}`,
        ]);
        this._cruiseSearchService.updateEngineCruiseOpeningStatus(false);
      });
  }
}
